import React from 'react';

import DeleteIcon from '../../assets/icons/miniBasket/Delete.svg';
import MinusIconSvg from '../../assets/icons/miniBasket/Minus.svg';
import PlusIconSvg from '../../assets/icons/miniBasket/Plus.svg';

export interface QuantityControlProps {
    quantity: number;
    onIncrease: () => void;
    onDecrease: () => void;
    disabled?: boolean;
    className?: string;
    size?: 'sm' | 'md' | 'lg';
    variant?: 'default' | 'miniBasket';
    decreaseIcon?: React.ReactNode;
}

/**
 * QuantityControl Component
 * Provides increment/decrement buttons for cart items.
 *
 * variant="miniBasket" – matches the mini-basket Figma spec:
 *   • buttons: 28×28px, border #D0D5DD, bg white
 *   • qty display: 40×28px, border #D0D5DD, color #002B66
 *   • qty=1 button shows DeleteIcon (16×18px); qty>1 shows Minus (16×18px)
 *   • Plus button icon: 16×18px, color #002B66
 */
const QuantityControl = ({
    quantity,
    onIncrease,
    onDecrease,
    disabled = false,
    className = '',
    size = 'md',
    variant = 'default',
    decreaseIcon,
}: QuantityControlProps) => {
    // ── Default variant sizes ─────────────────────────────────────────────
    const sizeClasses = {
        sm: 'w-[20px] h-[20px]',
        md: 'w-[28px] h-[28px]',
        lg: 'w-[36px] h-[36px]',
    };

    const containerSizeClasses = {
        sm: 'w-[30px] h-[20px] text-[12px]',
        md: 'w-[40px] h-[28px] text-[14px]',
        lg: 'w-[50px] h-[36px] text-[16px]',
    };

    // ── miniBasket variant ────────────────────────────────────────────────
    if (variant === 'miniBasket') {
        const btnStyle: React.CSSProperties = {
            width: 28,
            height: 28,
            padding: 4,
            backgroundColor: 'white',
            borderColor: '#D0D5DD',
        };
        const btnClass = `flex items-center justify-center rounded-[6px] border border-[#D0D5DD] transition-colors
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'}`;

        const qtyStyle: React.CSSProperties = {
            width: 40,
            height: 28,
            backgroundColor: 'white',
            color: '#002B66',
            fontWeight: 600,
            fontSize: 14,
            lineHeight: '20px',
        };

        return (
            <div className={`flex items-center gap-[2px] ${className}`}>
                {/* Delete / Decrement */}
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); if (!disabled) onDecrease(); }}
                    disabled={disabled}
                    className={btnClass}
                    style={quantity <= 1 ? btnStyle : { ...btnStyle, width: 28, padding: 0 }}
                    aria-label={quantity <= 1 ? 'Remove item' : 'Decrease quantity'}
                >
                    {quantity <= 1 ? (
                        decreaseIcon || (
                            <span style={{ display: 'flex', padding: '1px 2px' }}>
                                <img src={DeleteIcon} alt="Delete" style={{ width: 16, height: 18 }} />
                            </span>
                        )
                    ) : (
                        <img src={MinusIconSvg} alt="Decrease" style={{ width: 28, height: 28, objectFit: 'contain' }} />
                    )}
                </button>

                {/* Quantity display */}
                <div
                    className="flex items-center justify-center rounded-[6px] border border-[#D0D5DD]"
                    style={qtyStyle}
                >
                    {quantity}
                </div>

                {/* Increment */}
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); if (!disabled) onIncrease(); }}
                    disabled={disabled}
                    className={btnClass}
                    style={{ ...btnStyle, width: 28, padding: 0, color: '#002B66' }}
                    aria-label="Increase quantity"
                >
                    <img src={PlusIconSvg} alt="Add" style={{ width: 28, height: 28, objectFit: 'contain' }} />
                </button>
            </div>
        );
    }

    // ── Default variant ───────────────────────────────────────────────────
    return (
        <div className={`flex items-center gap-[4px] ${className}`}>
            <button
                onClick={(e) => { e.stopPropagation(); if (!disabled) onDecrease(); }}
                disabled={disabled}
                className={`${sizeClasses[size]} flex items-center justify-center border border-[#EAECF0] rounded-[6px] text-[#182230] transition-colors font-medium
                    ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'hover:bg-gray-50 cursor-pointer'}`}
                aria-label={quantity <= 1 ? 'Remove item' : 'Decrease quantity'}
            >
                <div className="w-full h-full flex items-center justify-center">
                    {quantity <= 1 ? (
                        decreaseIcon || <img src={DeleteIcon} alt="Remove" style={{ width: 16, height: 18 }} />
                    ) : (
                        <img src={MinusIconSvg} alt="Decrease" style={{ width: 28, height: 28, objectFit: 'contain' }} />
                    )}
                </div>
            </button>
            <div className={`${containerSizeClasses[size]} px-[3px] py-[1px] flex items-center justify-center border border-[#EAECF0] rounded-[6px] font-sans font-normal leading-tight text-[#182230]
                ${disabled ? 'bg-gray-100' : 'bg-white'}`}>
                {quantity}
            </div>
            <button
                onClick={(e) => { e.stopPropagation(); if (!disabled) onIncrease(); }}
                disabled={disabled}
                className={`${sizeClasses[size]} flex items-center justify-center border border-[#EAECF0] rounded-[6px] text-[#182230] transition-colors font-medium
                    ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'hover:bg-gray-50 cursor-pointer'}`}
                aria-label="Increase quantity"
            >
                <div className="w-full h-full flex items-center justify-center">
                    <img src={PlusIconSvg} alt="Add" style={{ width: 28, height: 28, objectFit: 'contain' }} />
                </div>
            </button>
        </div>
    );
};

export default QuantityControl;
